import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, firstValueFrom, forkJoin, map, of, take, tap } from 'rxjs';
import { Brand, CountryCurrency, Mall, ItemCategory, PriceCategory, Color, Warehouse, Store, ItemStyle, Collection, ItemCategoryVariance, ItemSize, Supplier, Tag } from './commondata.types';
import { CountryService } from 'app/modules/warehouse_inventory/swims+/management/country/country.service';
import { BrandService } from 'app/modules/warehouse_inventory/swims+/management/brand/brand.service';
import { MallService } from 'app/modules/warehouse_inventory/swims+/management/mall/mall.service';
import { pageField } from '@common/types/pagefield.types';
import { ItemCategoryService } from 'app/modules/warehouse_inventory/swims+/management/itemcategory/itemcategory.service';
import { SellPriceService } from 'app/modules/warehouse_inventory/swims+/management/sellprice/sellprice.service';
import { ColorService } from 'app/modules/warehouse_inventory/swims+/management/color/color.service';
import { WarehouseService } from 'app/modules/warehouse_inventory/swims+/management/warehouse/warehouse.service';
import { StoreService } from 'app/modules/warehouse_inventory/swims+/management/store/store.service';
import { ItemStyleService } from 'app/modules/warehouse_inventory/swims+/management/itemstyle/itemstyle.service';
import { CollectionService } from 'app/modules/warehouse_inventory/swims+/management/collection/collection.service';
import { ItemCategoryVarianceService } from 'app/modules/warehouse_inventory/swims+/management/itemcategoryvariance/itemcategoryvariance.service';
import { ItemSizeService } from 'app/modules/warehouse_inventory/swims+/management/itemsize/itemsize.service';
import { SupplierService } from 'app/modules/warehouse_inventory/swims+/management/supplier/supplier.service';
import { TagService } from 'app/modules/warehouse_inventory/swims+/management/tag/tag.service';
import { RoleManagementService } from 'app/modules/warehouse_inventory/swims/usermanagement/rolemanagement/rolemanagement.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { UserService } from 'app/core/user/user.service';
import { GoodsInboundService } from 'app/modules/warehouse_inventory/swims/tunnelscanning/goodsinbound/goodsinbound.service';

@Injectable({ providedIn: 'root' })
export class CommonDataService {
    private _systemStatues: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);
    private _myBrands: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);
    private _myStores: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);

    /**
     * Constructor
     */
    constructor(
        private _countryService: CountryService,
        private _brandService: BrandService,
        private _mallService: MallService,
        private _itemCategoryService: ItemCategoryService,
        private _itemCategoryVarianceService: ItemCategoryVarianceService,
        private _itemStyleService: ItemStyleService,
        private _itemSizeService: ItemSizeService,
        private _supplierService: SupplierService,
        private _sellPriceService: SellPriceService,
        private _collectionService: CollectionService,
        private _tagService: TagService,
        private _colorService: ColorService,
        private _warehouseLocationService: WarehouseService,
        private _storeService: StoreService,
        private _userRoleService: RoleManagementService,
        private _httpClient: HttpClient,
        private _userservice: UserService,
        private _goodsInboundService: GoodsInboundService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for Observable country and currency
     */
    get countrycurrency$(): Observable<CountryCurrency[]> {
        return this._countryService.countrycurrency$;
    }


    /**
     * Getter for country and currency
     */
    get countrycurrency(): CountryCurrency[] {
        return this._countryService.countrycurrency;
    }
    myCountries$ = this._userservice.user$.pipe(
        map(user => {
            // If no user → return all countries
            if (!user) {
                return this._countryService.countrycurrency;
            }

            // HQ → return all
            if (user.userTypeCode === 'HQ') {
                return this._countryService.countrycurrency;
            }

            // COUNTRY → filter or return empty
            if (user.userTypeCode === 'COUNTRY') {
                if (user.countryId) {
                    return this._countryService.countrycurrency.filter(c => c.id === user.countryId);
                }
                return [];
            }

            // Default → all
            return this._countryService.countrycurrency;
        })
    );

    /**
     * Getter for Observable country and currency
     */
    get brand$(): Observable<Brand[]> {
        return this._brandService.brand$;
    }

    /**
     * Getter for country and currency
     */
    get brand(): Brand[] {
        return this._brandService.brand;
    }

    /**
     * Getter for Observable color
     */
    get color$(): Observable<Color[]> {
        return this._colorService.color$;
    }

    /**
     * Getter for color
     */
    get color(): Color[] {
        return this._colorService.color;
    }

    /**
     * Getter for Observable collection
     */
    get collection$(): Observable<Collection[]> {
        return this._collectionService.collection$;
    }

    /**
     * Getter for collection
     */
    get collection(): Collection[] {
        return this._collectionService.collection;
    }

    /**
     * Getter for Observable country and currency
     */
    get mall$(): Observable<Mall[]> {
        return this._mallService.mall$;
    }

    /**
     * Getter for country and currency
     */
    get mall(): Mall[] {
        return this._mallService.mall;
    }

    /**
     * Getter for Observable store
     */
    get store$(): Observable<Store[]> {
        return this._storeService.store$;
    }

    /**
     * Getter for store
     */
    get store(): Store[] {
        return this._storeService.store;
    }

    /**
     * Getter for Observable ItemCategory
     */
    get itemCategory$(): Observable<ItemCategory[]> {
        return this._itemCategoryService.itemCategory$;
    }

    /**
     * Getter for ItemCategory
     */
    get itemCategory(): ItemCategory[] {
        return this._itemCategoryService.itemCategory;
    }

    /**
     * Getter for Observable ItemCategory
     */
    get itemCategoryVariance$(): Observable<ItemCategoryVariance[]> {
        return this._itemCategoryVarianceService.itemCategoryVariance$;
    }

    /**
     * Getter for ItemCategory
     */
    get itemCategoryVariance(): ItemCategoryVariance[] {
        return this._itemCategoryVarianceService.itemCategoryVariance;
    }

    /**
     * Getter for Observable ItemStyle
     */
    get itemStyle$(): Observable<ItemStyle[]> {
        return this._itemStyleService.itemstyle$;
    }

    /**
     * Getter for ItemStyle
     */
    get itemStyle(): ItemStyle[] {
        return this._itemStyleService.itemstyle;
    }

    /**
     * Getter for Observable ItemStyle
     */
    get itemSize$(): Observable<ItemSize[]> {
        return this._itemSizeService.itemSize$;
    }

    /**
     * Getter for ItemStyle
     */
    get itemSize(): ItemSize[] {
        return this._itemSizeService.itemSize;
    }

    /**
     * Getter for Observable PriceCategory
     */
    get priceCategory$(): Observable<PriceCategory[]> {
        return this._sellPriceService.priceCategory$;
    }

    /**
     * Getter for PriceCategory
     */
    get priceCategory(): PriceCategory[] {
        return this._sellPriceService.priceCategory;
    }

    /**
     * Getter for Observable Warehouse Location
     */
    get warehouseLocation$(): Observable<Warehouse[]> {
        return this._warehouseLocationService.warehouse$;
    }

    /**
     * Getter for Warehouse Location
     */
    get warehouseLocation(): Warehouse[] {
        return this._warehouseLocationService.warehouse;
    }

    /**
     * Getter for Observable Supplier
     */
    get supplier$(): Observable<Supplier[]> {
        return this._supplierService.supplier$;
    }

    /**
     * Getter for Supplier
     */
    get supplier(): Supplier[] {
        return this._supplierService.supplier;
    }

    /**
     * Getter for Observable Tag
     */
    get tag$(): Observable<Tag[]> {
        return this._tagService.tag$;
    }

    /**
     * Getter for Tag
     */
    get tag(): Tag[] {
        return this._tagService.tag;
    }

    /**
    * Getter for Observable country and currency
    */
    get userRoles$(): Observable<any[]> {
        return this._userRoleService.userRoles$;
    }
    /**
   * Getter for Observable Tunnel Codes
   */
    get tunnels$(): Observable<any[]> {
        return this._goodsInboundService.tunnels$;
    }

    /**
     * Getter for country and currency
     */
    get userRoles(): any[] {
        return this._userRoleService.userRoles;
    }
    //setter for systemstatues
    setSystemStatuses$(value: any[]) {
        this._systemStatues.next(value);
    }
    /**
     * Getter for Observable systemstatus
     */
    get systemStatuses$(): Observable<any[]> {
        return this._systemStatues.asObservable();
    }
    /**
     * Getter for systemstatus
     */
    get systemStatuses(): any[] {
        return this._systemStatues.value;
    }
    get myBrands(): any[] {
        return this._myBrands.value;
    }
    get myStores(): any[] {
        return this._myStores.value;
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all
     */
    getAll(): Observable<any> {
        return forkJoin({
            countrycurrencies: this._countryService.getCommonData(),
            //brands: this._brandService.getCommonData(),
            //malls: this._mallService.getCommonData(),
            //itemCategories: this._itemCategoryService.getCommonData(),
            //itemCategoryVariances: this._itemCategoryVarianceService.getCommonData(),
            //itemStyles: this._itemStyleService.getCommonData(),
            //itemSizes: this._itemSizeService.getCommonData(),
            //suppliers: this._supplierService.getCommonData(),
            //priceCategories: this._sellPriceService.getCommonData(),
            //colors: this._colorService.getCommonData(),
            //collections: this._collectionService.getCommonData(),
            //warehouseLocations: this._warehouseLocationService.getCommonData(),
            //stores: this._storeService.getCommonData(),
            //tags: this._tagService.getCommonData(),
            //userRoles: this._userRoleService.getCommonData(),
            //systemStatuses: this.getProducts(), needed for report , but now don't use this 
            myBrands: this.getMyBrands(),
            myStores: this.getStoresCurrentUser(),
            tunnel: this._goodsInboundService.getTunnelCommon()
        }).pipe(
            tap((response) => {

            })
        );
    }
    getProducts() {
        return this._httpClient.get<any[]>
            (`${environment.oDataUrl}/api/common/systemstatus`).pipe(map((response) => {
                this._systemStatues.next(response["value"]);
            }));
    }
    getStoresCurrentUser() {
        return this._httpClient.get<any[]>
            (`${environment.oDataUrl}/api/store/GetUserOutlets`).pipe(map((response) => {
                this._myStores.next(response);
            }));
    }
    /**
  * Get for data by name
  * @param 
  */
    getValueByString(name) {
        switch (name) {
            case 'mall':
                return this.mall;
            case 'brand':
                return this.brand;
            case 'countryCurrency':
            case 'countrySellPrices':
            case 'country':
            case 'currency':
                return this.countrycurrency;
            case 'collection':
                return this.collection;
            case 'itemCategory':
                return this.itemCategory;
            case 'itemCategoryVarianceManagement':
                return this.itemCategoryVariance;
            case 'itemStyle':
                return this.itemStyle;
            case 'itemSize':
                return this.itemSize;
            case 'priceCategory':
                return this.priceCategory;
            case 'color':
                return this.color;
            case 'warehouseLocation':
                return this._warehouseLocationService.warehouse;
            case 'store':
                return this.store;
            case 'supplier':
                return this.supplier;
            default:
                return undefined;
        }
    }

    /**
     * Get for data by name
     * @param 
     */
    getObservableByString(name) {
        switch (name) {
            case 'mall':
                return this.mall$;
            case 'brand':
                return this.brand$;
            case 'countryCurrency':
            case 'countrySellPrices':
            case 'country':
            case 'currency':
                return this.countrycurrency$;
            case 'collection':
                return this.collection$;
            case 'itemCategory':
                return this.itemCategory$;
            case 'itemCategoryVarianceManagement':
                return this.itemCategoryVariance$;
            case 'itemStyle':
                return this.itemStyle$;
            case 'itemSize':
                return this.itemSize$;
            case 'priceCategory':
                return this.priceCategory$;
            case 'color':
                return this.color$;
            case 'warehouseLocation':
                return this._warehouseLocationService.warehouse$;
            case 'store':
                return this.store$;
            case 'supplier':
                return this.supplier$;
            default:
                return undefined;
        }
    }
    async attachValidatorAsync(pagefields: pageField[]): Promise<pageField[]> {
        // use your existing getObs; shown here for completeness
        const getObs = (name: string): Observable<any[]> | undefined => {
            switch (name) {
                case 'brand': return this._brandService.getOrLoadBrands();
                case 'warehouseLocation': return this._warehouseLocationService.getOrLoadWarehouses();
                case 'supplier': return this._supplierService.getOrLoadSuppliers();
                case 'collection': return this._collectionService.getOrLoadCollection();
                case 'color': return this._colorService.getOrLoadColor();
                case 'store': return this._storeService.getOrLoadStores();
                case 'mall': return this._mallService.getOrLoadMalls();
                case 'itemCategory': return this._itemCategoryService.getOrLoadItemCategories();
                case 'itemCategoryVarianceManagement': return this._itemCategoryVarianceService.getOrLoadItemCategoryVariance();
                case 'itemStyle': return this._itemStyleService.getOrLoadItemStyles();
                case 'itemSize': return this._itemSizeService.getOrLoadItemSizes();
                case 'priceCategory': return this._sellPriceService.getOrLoadPriceCategory();
                case 'countryCurrency':
                case 'countrySellPrices':
                case 'country':
                case 'currency': return this._countryService.getOrLoadCountryCurrency();
                default: return undefined;
            }
        };

        // wait for first non-null emission from each source
        const toLoaded$ = (obs: Observable<any[]>) =>
            obs.pipe(filter(v => v != null), take(1), catchError(() => of([])));

        // collect unique source names ONLY for fields that have joiningKey
        const names = Array.from(
            new Set(
                pagefields
                    .filter(f => !!f.joiningKey)
                    .map(f => (f.colCode?.split('.')?.[0] ?? '').trim())
                    .filter(n => !!n)
            )
        );

        // build a dict of observables keyed by source name
        const obsDict: Record<string, Observable<any[]>> = {};
        for (const n of names) {
            const src$ = getObs(n);
            if (src$) obsDict[n] = toLoaded$(src$);
        }

        // resolve all needed sources in parallel (if none, empty dict)
        const dataDict: Record<string, any[]> = names.length
            ? await firstValueFrom(forkJoin(obsDict))
            : {};

        // attach validator ONLY to fields with joiningKey; others untouched
        return pagefields.map(f => {
            if (!f.joiningKey) return f; // skip fields without a join
            const sourceName = (f.colCode?.split('.')?.[0] ?? '').trim();
            const options = dataDict[sourceName];
            if (!options) return f;      // if no source found, leave as-is
            return { ...f, validator: options }; // your schema uses `validator` to hold options
        });
    }


    attachValidator(pagefield: pageField[]
    ): pageField[] {
        return pagefield.map((x) => {
            if (x.joiningKey) {
                x.validator = this.getValueByString(x.colCode.split('.')[0]);
            }
            return x;
        });
    }
    getMyBrands() {
        return this._httpClient.get<any[]>
            (`${environment.oDataUrl}/api/user/GetLoginUserBrands`).pipe(map((response) => {
                this._myBrands.next(response);
            }));
    }
}